/**
 * AnnouncementDialog — Cinematic 3-D premium announcement deck.
 *
 * Architecture — two layers of motion per card, cleanly separated:
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │  Outer motion.div  –  SLOT LAYER                          │
 *   │    • initial / animate / exit                              │
 *   │    • slot placement  : center / left / right               │
 *   │    • fan depth       : scale, rotateY, rotateZ, z, opacity │
 *   │    • transformPerspective + preserve-3d                    │
 *   │                                                            │
 *   │    ┌──────────────────────────────────────────────────┐    │
 *   │    │  Idle motion.div (centre only, post-entry)       │    │
 *   │    │    • subtle y bob (respects reduced-motion)      │    │
 *   │    │                                                   │    │
 *   │    │    ┌─────────────────────────────────────────┐   │    │
 *   │    │    │  Inner motion.div  –  DRAG LAYER        │   │    │
 *   │    │    │    • motion values x / y                │   │    │
 *   │    │    │    • rotateX / rotateY from drag offset │   │    │
 *   │    │    │    • drag, constraints, elastic,        │   │    │
 *   │    │    │      onDragEnd, onTap (< 8 px)          │   │    │
 *   │    │    │    • border glow, release ring, ghosts, │   │    │
 *   │    │    │      moving shine                        │   │    │
 *   │    │    └─────────────────────────────────────────┘   │    │
 *   │    └──────────────────────────────────────────────────┘    │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Nothing else is rendered — no close button, no indicator pill, no
 * sparkles, no radial glow, no table shadow, no instruction pill. Just
 * the cards on the default dialog backdrop.
 *
 * The cards sit centred in the viewport (flex items-center). Bottoms
 * still align because every card's rotation pivot is `50% 100%` and
 * their natural rest y is identical.
 *
 * API preserved:
 *   GET  /announcements              • GET /announcements?force=true
 *   POST /announcements/{id}/view    • `forceOpen` prop
 *   image preload + `IMAGE_CACHE`    • localStorage throttle
 */
import { useEffect, useState, useCallback, useRef } from "react";
import {
    motion,
    AnimatePresence,
    useMotionValue,
    useTransform,
    useMotionTemplate,
    useReducedMotion,
    animate,
} from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import api from "@/services/api";

const LAST_SHOWN_KEY = "announcement_last_shown";
const SHOW_INTERVAL_HOURS = 1;
const IMAGE_CACHE = new Set();
const SWIPE_THRESHOLD = 110;
const SWIPE_VELOCITY = 500;

const preloadImage = (src) =>
    new Promise((resolve) => {
        if (!src || IMAGE_CACHE.has(src)) return resolve(src);
        const img = new Image();
        img.onload = () => {
            IMAGE_CACHE.add(src);
            resolve(src);
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });

// Resolves whatever the admin saved into something the browser will load.
//
// Accepts:
//   • absolute https://… URLs               → returned untouched
//   • data:… URIs                           → returned untouched
//   • /api/uploads/…                        → prepended with REACT_APP_BACKEND_URL
//   • /uploads/… (legacy public path)       → rewritten to /api/uploads/… so it
//                                              routes through the K8s ingress
//   • bare /relative or relative paths      → prefixed with REACT_APP_BACKEND_URL
const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    const raw = String(imageUrl).trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^data:/i.test(raw)) return raw;
    let path = raw;
    // Legacy public_url paths (`/uploads/…`) get rewritten to `/api/uploads/…`
    // so they route through the Kubernetes ingress to the backend
    // (the bare `/uploads/…` prefix hits the frontend container).
    if (path.startsWith("/uploads/")) path = `/api${path}`;
    if (!path.startsWith("/")) path = `/${path}`;
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
};

// Normalises a redirect URL the admin typed. Mirrors the backend's
// _normalize_redirect_url so legacy data still clicks through cleanly.
const normalizeRedirectUrl = (raw) => {
    if (!raw) return null;
    const url = String(raw).trim();
    if (!url) return null;
    if (/^[a-z][a-z0-9+\-.]*:\/\//i.test(url)) return url; // http(s)://, ftp://…
    if (/^(mailto:|tel:|sms:)/i.test(url)) return url;
    if (url.startsWith("/") || url.startsWith("#")) return url;
    // Looks like a bare domain — auto-prefix https.
    if (url.includes(".") && !url.includes(" ")) return `https://${url}`;
    return `/${url}`;
};

export default function AnnouncementDialog({
    isLoggedIn = false,
    forceOpen = false,
    onForceClose = null,
    onOpened = null,
}) {
    // Full ordered list of announcements + an index pointing at the
    // current centre card. The two peeks are derived:
    //   right = list[idx + 1]   (more visible, promotes next)
    //   left  = list[idx + 2]   (deeper back-of-deck, rises to right)
    const [list, setList] = useState([]);
    const [idx, setIdx] = useState(0);

    const [isOpen, setIsOpen] = useState(false);
    const hasFetched = useRef(false);
    const reduced = useReducedMotion();

    // Pre-load every image and DROP announcements whose image is broken/
    // missing so the carousel never shows an empty yellow card. Returns
    // the filtered list (announcements with a working image, or no image
    // at all — text-only announcements still render).
    const preloadAndFilter = useCallback(async (items) => {
        if (!items?.length) return [];
        const checks = await Promise.all(
            items.map(async (a) => {
                if (!a?.image_url) return a; // text-only — keep
                const url = getImageUrl(a.image_url);
                const ok = await preloadImage(url);
                return ok ? a : null;
            }),
        );
        return checks.filter(Boolean);
    }, []);

    const shouldShow = () => {
        try {
            const last = localStorage.getItem(LAST_SHOWN_KEY);
            if (!last) return true;
            const hours = (Date.now() - new Date(last).getTime()) / 3_600_000;
            return hours >= SHOW_INTERVAL_HOURS;
        } catch {
            return true;
        }
    };

    const stampShown = () => {
        try {
            localStorage.setItem(LAST_SHOWN_KEY, new Date().toISOString());
        } catch {
            /* noop */
        }
    };

    useEffect(() => {
        if (forceOpen) {
            fetchForced();
            return;
        }
        const t = setTimeout(() => {
            if (!hasFetched.current && shouldShow()) {
                hasFetched.current = true;
                fetchNormal();
            }
        }, 1500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, forceOpen]);

    const openWithList = async (items) => {
        // Drop entries whose hero image cannot be loaded — prevents
        // the empty yellow card UX when an upload binary is missing
        // (e.g. after a fresh DB restore without the uploads folder).
        const safeItems = await preloadAndFilter(items);
        if (!safeItems.length) {
            onForceClose?.();
            onOpened?.();
            return;
        }
        setList(safeItems);
        setIdx(0);
        setIsOpen(true);
        onOpened?.();
    };

    const fetchForced = async () => {
        try {
            const r = await api.get("/announcements?force=true");
            await openWithList((r.data || r)?.announcements || []);
        } catch (e) {
            console.error("announcements forced fetch failed", e);
            onForceClose?.();
            onOpened?.();
        }
    };

    const fetchNormal = async () => {
        try {
            const r = await api.get("/announcements");
            const list = (r.data || r)?.announcements || [];
            if (list.length) {
                await openWithList(list);
                stampShown();
            }
        } catch (e) {
            console.error("announcements fetch failed", e);
        }
    };

    const markAsSeen = async (id) => {
        try {
            await api.post(`/announcements/${id}/view`);
        } catch {
            /* noop */
        }
    };

    const dismissCenter = () => {
        const top = list[idx];
        if (!top) return;
        markAsSeen(top.id);
        // Index shift. Each card's component (keyed by id) keeps its
        // identity: list[idx+1] was in the "right" slot and its animate
        // target now changes to "center" → Framer springs it in cleanly.
        // list[idx+2] was in "left" and its target becomes "right" →
        // it glides up the deck. A new list[idx+3] (if any) enters at
        // "left" with its initial fly-in animation.
        const nextIdx = idx + 1;
        if (nextIdx >= list.length) {
            // No more cards — close.
            setIsOpen(false);
            setList([]);
            setIdx(0);
            onForceClose?.();
            return;
        }
        setIdx(nextIdx);
    };

    const handleClose = () => {
        // Mark every currently-visible card as seen.
        [list[idx], list[idx + 1], list[idx + 2]].forEach(
            (a) => a?.id && markAsSeen(a.id),
        );
        setIsOpen(false);
        setList([]);
        setIdx(0);
        onForceClose?.();
    };

    const handleCardTap = (a) => {
        if (!a?.redirect_url) return;
        const target = normalizeRedirectUrl(a.redirect_url);
        if (!target) return;
        markAsSeen(a.id);
        // External URLs (anything with a scheme) → new tab, never replace
        // the in-app session. Internal app routes ("/foo", "#bar")
        // navigate in the same tab so React Router picks them up.
        const isExternal = /^[a-z][a-z0-9+\-.]*:/i.test(target);
        if (isExternal) {
            window.open(target, "_blank", "noopener,noreferrer");
        } else {
            window.location.href = target;
        }
    };

    const centerAnn = list[idx];
    if (!isOpen || !centerAnn) return null;

    // Each visible card carries its absolute `position` (its index in
    // the full list). Centre enters from the top; peeks alternate entry
    // side based on `position % 2`.
    const slots = [{ slot: "center", a: centerAnn, position: idx }];
    if (list[idx + 1])
        slots.push({ slot: "right", a: list[idx + 1], position: idx + 1 });
    if (list[idx + 2])
        slots.push({ slot: "left", a: list[idx + 2], position: idx + 2 });

    return (
        <Dialog open={isOpen} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent
                // Full-viewport transparent stage, no chrome, hide shadcn's X
                className="max-w-none !w-screen !h-screen !top-0 !left-0 !translate-x-0 !translate-y-0 !rounded-none p-0 gap-0 bg-transparent border-0 shadow-none overflow-hidden [&>button]:hidden"
                aria-describedby="announcement-stage"
                data-testid="announcement-stack"
            >
                <div
                    className="relative w-full h-full flex items-center justify-center"
                    style={{
                        perspective: "1800px",
                        touchAction: "none", // allow vertical/horizontal drag without page scroll stealing
                    }}
                >
                    <div
                        className="relative w-[88vw] max-w-[22rem]"
                        style={{
                            height: "min(65vh, 560px)",
                            transformStyle: "preserve-3d",
                        }}
                    >
                        <AnimatePresence initial={false}>
                            {slots.map(({ slot, a, position }) => (
                                <FanCard
                                    key={a.id}
                                    slot={slot}
                                    a={a}
                                    position={position}
                                    reduced={reduced}
                                    onDismiss={dismissCenter}
                                    onTap={handleCardTap}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ---------------------------------------------------- SLOT GEOMETRY */

const SLOT_REST = {
    center: {
        x: "0%",
        y: "0%",
        scale: 1,
        rotateX: 0,
        rotateY: 0,
        rotateZ: 0,
        z: 90,
        opacity: 1,
    },
    right: {
        x: "9%",
        y: "3%",
        scale: 0.93,
        rotateX: 3,
        rotateY: -20,
        rotateZ: 8,
        z: -70,
        opacity: 0.75,
    },
    left: {
        x: "-9%",
        y: "6%",
        scale: 0.86,
        rotateX: 5,
        rotateY: 20,
        rotateZ: -8,
        z: -150,
        opacity: 0.55,
    },
};

// Entry animation — every card flies in from a SIDE (left / right)
// whose parity matches its absolute position, so the deck assembles
// with a visible left-right alternation. The hero (idx = 0, even) also
// comes from the left to match the pattern rather than dropping from
// the top — the user asked for a consistent "from left and right"
// feel, not a random direction per slot.
const getInitial = (_slot, position) => {
    const fromLeft = position % 2 === 0;
    return {
        x: fromLeft ? "-150%" : "150%",
        y: "15%",
        scale: 0.28,
        rotateX: fromLeft ? -12 : 18,
        rotateY: fromLeft ? 55 : -55,
        rotateZ: fromLeft ? 32 : -28,
        z: -280,
        opacity: 0,
    };
};

// Dramatic assembly — deepest card lands first, hero lands LAST. The
// gaps are long enough that the viewer tracks each arrival individually
// but short enough that the whole deck is ready within ~1.2 s — no
// artificial wait before the user can interact.
const SLOT_DELAY = { left: 0, right: 0.35, center: 0.7 };

// Slow-ish spring so each card's landing is visible, not a snap.
const ENTRY_TRANSITION = {
    type: "spring",
    stiffness: 140,
    damping: 20,
    mass: 1.0,
};

/* ---------------------------------------------------- FanCard */

function FanCard({ slot, a, position, reduced, onDismiss, onTap }) {
    const imgUrl = getImageUrl(a.image_url);
    const isCenter = slot === "center";
    const rest = SLOT_REST[slot];
    const initial = reduced
        ? { opacity: 0, scale: 1, x: rest.x, y: 0 }
        : getInitial(slot, position);
    const animateRest = reduced
        ? { ...rest, rotateX: 0, rotateY: 0, rotateZ: 0, z: 0 }
        : rest;

    // Drag motion values live on the INNER div, but must be declared
    // unconditionally (hook rules). Side cards simply don't read them.
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateYDrag = useTransform(x, [-220, 220], [-18, 18]);
    const rotateXDrag = useTransform(y, [-220, 220], [16, -16]);
    const dragMag = useTransform([x, y], ([xv, yv]) => Math.hypot(xv, yv));

    const releaseOpacity = useTransform(
        dragMag,
        [0, SWIPE_THRESHOLD * 0.55, SWIPE_THRESHOLD],
        [0, 0.15, 0.85],
    );
    const glowStrength = useTransform(dragMag, [0, SWIPE_THRESHOLD], [0.35, 1]);
    const glowShadow = useMotionTemplate`0 0 0 1px rgba(253,224,71,${glowStrength}), 0 18px 40px -12px rgba(251,191,36,${glowStrength})`;

    const ghost1Opacity = useTransform(
        dragMag,
        [0, SWIPE_THRESHOLD],
        [0, 0.35],
    );
    const ghost1X = useTransform(x, [-220, 220], [14, -14]);
    const ghost1Y = useTransform(y, [-220, 220], [10, -10]);
    const ghost2Opacity = useTransform(dragMag, [0, SWIPE_THRESHOLD], [0, 0.2]);
    const ghost2X = useTransform(x, [-220, 220], [28, -28]);
    const ghost2Y = useTransform(y, [-220, 220], [18, -18]);

    // Drag is available IMMEDIATELY — no wait timer.
    const ready = true;

    // First mount uses ENTRY_TRANSITION (with the staggered entry delay);
    // every transition AFTER that (e.g. right-peek promoting to centre
    // when the user swipes) uses a fast spring with NO delay so the
    // next card rises forward the instant the hero leaves.
    const [entered, setEntered] = useState(false);
    useEffect(() => {
        const ms = ((SLOT_DELAY[slot] ?? 0) + 0.7) * 1000;
        const t = setTimeout(() => setEntered(true), ms);
        return () => clearTimeout(t);
    }, [slot]);
    // Latched exit direction read by the outer's exit prop
    const [exitInfo, setExitInfo] = useState({ x: 800, y: 0, rz: 28 });
    const didDismiss = useRef(false);

    const handleDragEnd = (_, info) => {
        const dx = info.offset.x;
        const dy = info.offset.y;
        const vx = info.velocity.x;
        const vy = info.velocity.y;
        const mag = Math.hypot(dx, dy);
        const vmag = Math.hypot(vx, vy);
        if (mag > SWIPE_THRESHOLD || vmag > SWIPE_VELOCITY) {
            const total = mag || 1;
            const nx = dx / total;
            const ny = dy / total;
            didDismiss.current = true;
            setExitInfo({
                x: nx * 1200,
                y: ny * 1200,
                rz: Math.max(-45, Math.min(45, dx * 0.06 + dy * 0.02)),
            });
            requestAnimationFrame(() => {
                onDismiss();
            });
            return;
        }
        animate(x, 0, { type: "spring", stiffness: 420, damping: 28 });
        animate(y, 0, { type: "spring", stiffness: 420, damping: 28 });
    };

    // TAP — Framer Motion's onTap only fires when the gesture wasn't a
    // drag, so we don't need to inspect pointer offsets. Extra safety
    // via didDismiss in case a swipe-dismiss just happened.
    const handleTap = () => {
        if (didDismiss.current) return;
        onTap(a);
    };

    return (
        // ─── OUTER: slot layer (placement / entry / exit / idle) ─────
        <motion.div
            layout
            className="absolute inset-0 flex items-end justify-center"
            style={{
                zIndex: isCenter ? 30 : slot === "right" ? 20 : 10,
                pointerEvents: isCenter ? "auto" : "none",
                transformStyle: "preserve-3d",
                transformPerspective: 1400,
                transformOrigin: "50% 100%",
            }}
            initial={initial}
            animate={animateRest}
            exit={{
                x: exitInfo.x,
                y: exitInfo.y,
                rotateZ: exitInfo.rz,
                opacity: 0,
                scale: 0.78,
                transition: { duration: 0.42, ease: [0.4, 0, 0.2, 1] },
            }}
            transition={
                entered
                    ? { type: "spring", stiffness: 260, damping: 24 }
                    : { ...ENTRY_TRANSITION, delay: SLOT_DELAY[slot] }
            }
            data-testid={`announcement-card-${slot}`}
        >
            {/* ─── IDLE: subtle float on centre once entry is settled ─── */}
            <motion.div
                className="w-full h-full flex items-center justify-center"
                style={{ transformStyle: "preserve-3d" }}
                animate={
                    isCenter && ready && !reduced ? { y: [0, -6, 0] } : { y: 0 }
                }
                transition={
                    isCenter && ready && !reduced
                        ? {
                              duration: 4.2,
                              repeat: Infinity,
                              ease: "easeInOut",
                          }
                        : { duration: 0 }
                }
            >
                {/* ─── INNER: drag layer (centre only) ─── */}
                <motion.div
                    className={`relative aspect-[3/4] w-full max-w-[86%] touch-none ${isCenter && a?.redirect_url ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"}`}
                    style={
                        isCenter
                            ? {
                                  x,
                                  y,
                                  rotateX: rotateXDrag,
                                  rotateY: rotateYDrag,
                                  transformStyle: "preserve-3d",
                                  transformPerspective: 1400,
                                  touchAction: "none",
                                  willChange: "transform",
                              }
                            : {
                                  transformStyle: "preserve-3d",
                                  willChange: "transform",
                              }
                    }
                    drag={isCenter && ready}
                    dragDirectionLock={false}
                    dragElastic={0.18}
                    dragMomentum={false}
                    whileDrag={{ cursor: "grabbing", scale: 1.03 }}
                    onDragStart={() => {
                        didDismiss.current = false;
                    }}
                    onDragEnd={isCenter ? handleDragEnd : undefined}
                    onTap={isCenter ? handleTap : undefined}
                >
                    {/* Ghost trails behind the dragged card */}
                    {isCenter && (
                        <>
                            <motion.div
                                className="absolute inset-0 rounded-[26px] border border-white/20 pointer-events-none"
                                style={{
                                    opacity: ghost1Opacity,
                                    x: ghost1X,
                                    y: ghost1Y,
                                }}
                            />
                            <motion.div
                                className="absolute inset-0 rounded-[26px] border border-amber-300/30 pointer-events-none"
                                style={{
                                    opacity: ghost2Opacity,
                                    x: ghost2X,
                                    y: ghost2Y,
                                }}
                            />
                        </>
                    )}

                    {/* Card frame (gold/glass on hero, slate on peeks) */}
                    <div
                        className="relative h-full w-full rounded-[26px] p-[3px]"
                        style={{
                            background: isCenter
                                ? "linear-gradient(135deg, rgba(252,211,77,0.85), rgba(217,119,6,0.45) 40%, rgba(15,23,42,0.9) 70%, rgba(250,204,21,0.75))"
                                : "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(15,23,42,0.8) 55%, rgba(255,255,255,0.08))",
                            boxShadow: isCenter
                                ? "0 30px 60px -20px rgba(0,0,0,0.85)"
                                : "0 24px 48px -18px rgba(0,0,0,0.85)",
                            backfaceVisibility: "hidden",
                        }}
                    >
                        {/* Interactive gold border glow (drives with drag) */}
                        {isCenter && (
                            <motion.div
                                className="absolute -inset-[2px] rounded-[28px] pointer-events-none"
                                style={{ boxShadow: glowShadow }}
                            />
                        )}

                        {/* Release ring — appears as drag nears threshold */}
                        {isCenter && (
                            <motion.div
                                className="absolute -inset-[8px] rounded-[32px] border-2 border-amber-300/80 pointer-events-none"
                                style={{ opacity: releaseOpacity }}
                            />
                        )}

                        {/* Inner image plate */}
                        <div className="relative h-full w-full rounded-[22px] overflow-hidden ring-1 ring-white/10 bg-slate-800">
                            {imgUrl ? (
                                <img
                                    src={imgUrl}
                                    alt=""
                                    className="w-full h-full object-cover pointer-events-none select-none transition-opacity duration-300"
                                    draggable={false}
                                    loading="eager"
                                    decoding="async"
                                    onLoad={(e) => {
                                        e.currentTarget.style.opacity = "1";
                                    }}
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                    }}
                                    style={{ opacity: 1 }}
                                    data-testid={`announcement-image-${slot}`}
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-white/50 text-sm tracking-wide">
                                    {a.title || "Announcement"}
                                </div>
                            )}

                            {/* Inner vignette for depth */}
                            <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_60px_rgba(0,0,0,0.5)] rounded-[22px]" />

                            {/* Moving diagonal shine — centre only */}
                            {isCenter && !reduced && (
                                <motion.div
                                    className="absolute inset-0 pointer-events-none rounded-[22px]"
                                    style={{
                                        background:
                                            "linear-gradient(115deg, transparent 25%, rgba(255,255,255,0.38) 48%, transparent 68%)",
                                        mixBlendMode: "screen",
                                    }}
                                    initial={{ x: "-130%" }}
                                    animate={{ x: ["-130%", "130%"] }}
                                    transition={{
                                        duration: 2.6,
                                        repeat: Infinity,
                                        repeatDelay: 2.4,
                                        ease: "easeInOut",
                                    }}
                                />
                            )}

                            {/* "Tap to visit" CTA — only on the centre card
                                AND only when the admin has set a
                                redirect_url. Pointer-events-none so it
                                never steals taps from the parent draggable
                                layer; the parent's onTap handler still fires
                                handleCardTap which performs the redirect. */}
                            {isCenter && a?.redirect_url && (
                                <div
                                    className="absolute bottom-0 inset-x-0 px-3 pb-3 pt-10 pointer-events-none rounded-b-[22px]"
                                    style={{
                                        background:
                                            "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))",
                                    }}
                                    data-testid={`announcement-cta-${slot}`}
                                >
                                    <div className="flex items-center justify-center gap-1.5 text-amber-100 text-xs font-semibold uppercase tracking-[0.18em] drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                                        <span>Tap to visit</span>
                                        <span aria-hidden="true">→</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Dim veil on side cards (darker for "back-of-deck" feel) */}
                        {!isCenter && (
                            <div className="absolute inset-0 rounded-[26px] bg-black/45 pointer-events-none" />
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
}
